import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, DocumentArrowDownIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, XMarkIcon, ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import api from '../utils/api';

// Configurar el worker de PDF.js con una versión específica compatible
const pdfjs = await import('pdfjs-dist');
pdfjs.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.js';

const ApproveDeposits = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth() || { user: null, loading: true };
  const [comprobantes, setComprobantes] = useState([]);
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [isLoadingFetch, setIsLoadingFetch] = useState(false);
  const [isLoadingApprove, setIsLoadingApprove] = useState(false);
  const [error, setError] = useState(null);
  const [expandedMatrizador, setExpandedMatrizador] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [processingComprobante, setProcessingComprobante] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [fileError, setFileError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('Todos'); // Nuevo estado para el filtro

  // Cargar los días disponibles
  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response = await api.get('/api/audit/document-days/');
        setDays(response.data.days || []);
        if (response.data.days && response.data.days.length > 0) {
          const targetDay = response.data.days.find(day => day === '2025-05-12') || response.data.days[0];
          setSelectedDay(targetDay);
        }
      } catch (error) {
        toast.error('Error al cargar los días disponibles.', { position: 'top-right', autoClose: 5000 });
      }
    };
    if (!authLoading && user && user.is_notario) {
      fetchDays();
    }
  }, [user, authLoading]);

  // Validar permisos
  useEffect(() => {
    if (!authLoading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a esta página.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Función para cargar comprobantes manualmente
  const fetchComprobantes = useCallback(async () => {
    if (!selectedDay) {
      toast.error('Por favor, selecciona un día.', { position: 'top-right', autoClose: 3000 });
      return;
    }
    try {
      setIsLoadingFetch(true);
      setError(null);
      const response = await api.get('/api/audit/comprobantes/', {
        params: { fecha_documento: selectedDay, _t: Date.now() }, // Añadir timestamp para evitar caché
      });
      setComprobantes(response.data);
    } catch (err) {
      if (err.response) {
        setError('Error del servidor al cargar los comprobantes. Inténtalo de nuevo más tarde.');
      } else if (err.request) {
        setError('No se pudo conectar al servidor. Verifica tu conexión a internet.');
      } else {
        setError('Error al cargar los comprobantes: ' + err.message);
      }
    } finally {
      setIsLoadingFetch(false);
    }
  }, [selectedDay]);

  // Función para determinar el estado de un comprobante
  const getComprobanteStatus = (comprobante) => {
    if (comprobante.aprobado === true) {
      return { status: 'Aprobado', color: 'bg-green-500' };
    } else if (comprobante.aprobado === false && comprobante.notes) {
      return { status: 'Rechazado', color: 'bg-red-500' };
    }
    return { status: 'Pendiente', color: 'bg-yellow-500' };
  };

  // Agrupar comprobantes por matrizador y calcular estados
  const groupByMatrizador = useMemo(() => {
    const grouped = comprobantes.reduce((acc, comprobante) => {
      const statusInfo = getComprobanteStatus(comprobante);
      // Filtrar comprobantes según el estado seleccionado
      if (filterStatus !== 'Todos' && statusInfo.status !== filterStatus) {
        return acc;
      }

      const processedTramiteIds = new Set(); // Rastrear IDs de trámites únicos por comprobante

      comprobante.tramites.forEach(tramite => {
        const matrizador = tramite.usuario_matrizador || 'Sin Matrizador';
        if (!acc[matrizador]) {
          acc[matrizador] = {
            comprobantes: [],
            tramites: [],
            totalComisionAuto: 0,
            totalReconocimientos: 0,
          };
        }
        // Solo añadir el trámite si no ha sido procesado en este comprobante
        if (!processedTramiteIds.has(tramite.id)) {
          processedTramiteIds.add(tramite.id);
          // Solo añadir si no está ya en acc[matrizador].tramites
          if (!acc[matrizador].tramites.some(t => t.id === tramite.id)) {
            acc[matrizador].tramites.push({
              ...tramite,
              comision_auto: parseFloat(tramite.comision_auto || 0),
              total_ajustado: parseFloat(tramite.total_ajustado || 0),
              num_reconocimientos: parseInt(tramite.reconocimientos_count || 0),
            });
          }
          // Actualizar totales solo una vez por trámite único
          acc[matrizador].totalComisionAuto += parseFloat(tramite.comision_auto || 0);
          acc[matrizador].totalReconocimientos += parseInt(tramite.reconocimientos_count || 0);
        }
        if (!acc[matrizador].comprobantes.some(c => c.id === comprobante.id)) {
          acc[matrizador].comprobantes.push({
            ...comprobante,
            status: statusInfo.status,
            statusColor: statusInfo.color,
          });
        }
      });
      return acc;
    }, {});

    Object.keys(grouped).forEach(matrizador => {
      const { tramites, comprobantes } = grouped[matrizador];
      // Redondear los valores a 2 decimales para evitar errores de punto flotante
      const totalAjustado = parseFloat(
        tramites.reduce((sum, t) => sum + parseFloat(t.total_ajustado || 0), 0).toFixed(2)
      );
      // Solo sumar los montos de los comprobantes aprobados
      const totalComprobantes = parseFloat(
        comprobantes
          .filter(c => c.aprobado === true) // Filtrar solo comprobantes aprobados
          .reduce((sum, c) => sum + parseFloat(c.monto_total || 0), 0)
          .toFixed(2)
      );
      // Calcular la diferencia y redondearla a 2 decimales
      const difference = parseFloat((totalAjustado - totalComprobantes).toFixed(2));

      // Determinar el estado general del matrizador
      const allAprobados = comprobantes.every(c => c.status === 'Aprobado');
      const allRechazados = comprobantes.every(c => c.status === 'Rechazado');
      const allFinalizados = comprobantes.every(c => c.status === 'Aprobado' || c.status === 'Rechazado');
      let matrizadorStatus = '';
      let matrizadorStatusColor = '';
      let accountingStatus = '';
      let accountingStatusColor = '';

      // Calcular el estado contable (difference)
      // Usar una tolerancia para considerar difference como 0
      const isDifferenceZero = Math.abs(difference) < 0.01; // Tolerancia de 0.01
      if (isDifferenceZero) {
        accountingStatus = 'Cuadrado';
        accountingStatusColor = 'bg-green-500';
      } else if (comprobantes.length === 0) {
        accountingStatus = 'Pendiente';
        accountingStatusColor = 'bg-yellow-500';
      } else {
        // Asegurarse de que no se muestre "-0.00"
        const displayDifference = difference === 0 ? 0 : difference;
        accountingStatus = `Falta $${displayDifference.toFixed(2)}`;
        accountingStatusColor = 'bg-red-500';
      }

      // Calcular el estado de aprobación/rechazo
      if (isDifferenceZero && allAprobados) {
        matrizadorStatus = `Cuadrado (${comprobantes.length}/${comprobantes.length})`;
        matrizadorStatusColor = 'bg-green-500';
      } else if (allAprobados) {
        matrizadorStatus = `Todos Aprobados (${comprobantes.length}/${comprobantes.length})`;
        matrizadorStatusColor = 'bg-green-500';
      } else if (allRechazados) {
        matrizadorStatus = `Todos Rechazados (${comprobantes.length}/${comprobantes.length})`;
        matrizadorStatusColor = 'bg-red-500';
      } else if (allFinalizados) {
        const aprobadosCount = comprobantes.filter(c => c.status === 'Aprobado').length;
        matrizadorStatus = `Finalizados: ${aprobadosCount} Aprobados, ${comprobantes.length - aprobadosCount} Rechazados`;
        matrizadorStatusColor = 'bg-gray-500';
      } else {
        const pendientesCount = comprobantes.filter(c => c.status === 'Pendiente').length;
        matrizadorStatus = `Pendientes: ${pendientesCount}/${comprobantes.length}`;
        matrizadorStatusColor = 'bg-yellow-500';
      }

      grouped[matrizador].totalAjustado = totalAjustado;
      grouped[matrizador].totalComprobantes = totalComprobantes;
      grouped[matrizador].status = accountingStatus;
      grouped[matrizador].statusColor = accountingStatusColor;
      grouped[matrizador].matrizadorStatus = matrizadorStatus;
      grouped[matrizador].matrizadorStatusColor = matrizadorStatusColor;
      grouped[matrizador].allFinalizados = allFinalizados;
    });

    return grouped;
  }, [comprobantes, filterStatus]);

  // Aprobar un comprobante
  const handleApproveComprobante = async (comprobanteId, matrizador, isBulk = false) => {
    setProcessingComprobante(isBulk ? null : comprobanteId);
    setIsLoadingApprove(isBulk);
    try {
      await api.post('/api/audit/approve-comprobante/', {
        comprobante_id: comprobanteId,
      });
      setActionFeedback({
        type: 'success',
        message: isBulk 
          ? `Comprobantes de ${matrizador} aprobados exitosamente. (${groupByMatrizador[matrizador].tramites.length} trámites afectados)`
          : `Comprobante #${comprobanteId} aprobado exitosamente.`,
      });
      setTimeout(() => setActionFeedback(null), 2000);
      await fetchComprobantes();
    } catch (error) {
      const message = error.response?.data?.error || 'Error al aprobar el comprobante.';
      setActionFeedback({
        type: 'error',
        message: message,
      });
      setTimeout(() => setActionFeedback(null), 3000);
    } finally {
      setProcessingComprobante(null);
      setIsLoadingApprove(false);
    }
  };

  // Rechazar un comprobante
  const handleRejectComprobante = async (comprobanteId, matrizador, notes, isBulk = false) => {
    setProcessingComprobante(isBulk ? null : comprobanteId);
    setIsLoadingApprove(isBulk);
    try {
      await api.post('/api/audit/reject-comprobante/', {
        comprobante_id: comprobanteId,
        notes: notes,
      });
      setActionFeedback({
        type: 'success',
        message: isBulk 
          ? `Comprobantes de ${matrizador} rechazados exitosamente. (${groupByMatrizador[matrizador].tramites.length} trámites afectados)`
          : `Comprobante #${comprobanteId} rechazado exitosamente.`,
      });
      setTimeout(() => setActionFeedback(null), 2000);
      await fetchComprobantes();
    } catch (error) {
      const message = error.response?.data?.error || 'Error al rechazar el comprobante.';
      setActionFeedback({
        type: 'error',
        message: message,
      });
      setTimeout(() => setActionFeedback(null), 3000);
    } finally {
      setProcessingComprobante(null);
      setIsLoadingApprove(false);
      setConfirmAction(null);
      setRejectionNotes('');
    }
  };

  // Aprobar todos los comprobantes de un matrizador
  const handleApproveMatrizador = (matrizador) => {
    setConfirmAction({ type: 'approve', matrizador });
  };

  // Rechazar todos los comprobantes de un matrizador
  const handleRejectMatrizador = (matrizador) => {
    setConfirmAction({ type: 'reject', matrizador });
  };

  // Confirmar acción (aprobar o rechazar)
  const handleConfirmAction = () => {
    const { type, matrizador, comprobanteId } = confirmAction;
    if (comprobanteId) {
      if (type === 'approve') {
        handleApproveComprobante(comprobanteId, matrizador);
      } else {
        handleRejectComprobante(comprobanteId, matrizador, rejectionNotes);
      }
    } else {
      const comprobantesToProcess = groupByMatrizador[matrizador].comprobantes.filter(c => c.status === 'Pendiente');
      if (type === 'approve') {
        comprobantesToProcess.forEach(comprobante => {
          handleApproveComprobante(comprobante.id, matrizador, true);
        });
      } else {
        comprobantesToProcess.forEach(comprobante => {
          handleRejectComprobante(comprobante.id, matrizador, rejectionNotes, true);
        });
      }
    }
  };

  // Ver archivo (PDF o imagen)
  const handleViewFile = (fileUrl, isPdf) => {
    if (isPdf) {
      window.open(fileUrl, '_blank');
    } else {
      setSelectedFile(fileUrl);
      setShowFileViewer(true);
      setZoomLevel(1);
      setFileError(null);
    }
  };

  // Zoom in
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 3));
  };

  // Zoom out
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  // Manejo de error al cargar imagen
  const onImageLoadError = () => {
    setFileError('No se pudo cargar la imagen. Intenta descargarla directamente.');
    toast.error('Error al cargar la imagen.', { position: 'top-right' });
  };

  // Manejo de cambio de día
  const handleDayChange = (e) => {
    setSelectedDay(e.target.value);
    setComprobantes([]);
    setExpandedMatrizador(null);
    setFilterStatus('Todos'); // Reiniciar el filtro al cambiar de día
  };

  // Manejo de cambio de filtro
  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setExpandedMatrizador(null); // Contraer todos los matrizadores al cambiar el filtro
  };

  if (authLoading) {
    return <div className="text-white text-center">Cargando...</div>;
  }

  if (!user) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Aprobar Depósitos</h1>
        </div>
        <Link
          to="/dashboard/notario"
          className="bg-notaria-yellow text-notaria-blue-1 font-bold py-1.5 px-3 sm:py-2 sm:px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all duration-300"
          aria-label="Volver al Dashboard"
        >
          Volver al Dashboard
        </Link>
      </header>

      <main className="flex-1">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
        >
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Seleccionar Día y Filtro</h2>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
            <div className="w-full sm:w-1/3">
              <label htmlFor="fecha_documento" className="block text-sm sm:text-lg font-semibold mb-2">
                Día de los Comprobantes
              </label>
              <select
                id="fecha_documento"
                value={selectedDay}
                onChange={handleDayChange}
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              >
                <option value="">Seleccione un día</option>
                {days.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-1/3">
              <label htmlFor="filter_status" className="block text-sm sm:text-lg font-semibold mb-2">
                Filtrar por Estado
              </label>
              <select
                id="filter_status"
                value={filterStatus}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              >
                <option value="Todos">Todos</option>
                <option value="Pendiente">Pendientes</option>
                <option value="Aprobado">Aprobados</option>
                <option value="Rechazado">Rechazados</option>
              </select>
            </div>
            <motion.button
              onClick={fetchComprobantes}
              disabled={isLoadingFetch || !selectedDay}
              className={clsx(
                'bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg',
                'hover:bg-notaria-red hover:text-white transition-all',
                isLoadingFetch || !selectedDay ? 'opacity-50 cursor-not-allowed' : ''
              )}
              whileHover={{ scale: isLoadingFetch || !selectedDay ? 1 : 1.02 }}
              whileTap={{ scale: isLoadingFetch || !selectedDay ? 1 : 0.98 }}
            >
              {isLoadingFetch ? (
                <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
              ) : (
                'Cargar Comprobantes'
              )}
            </motion.button>
          </div>
        </motion.div>

        {isLoadingFetch ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-200"
          >
            Cargando comprobantes, por favor espera...
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-5xl mx-auto bg-red-500 text-white p-4 rounded-lg mb-6 sm:mb-8"
          >
            {error}
          </motion.div>
        ) : (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-xl sm:text-2xl font-semibold mb-6 sm:mb-8 text-gray-200">Historial de Depósitos por Matrizador</h2>
            {actionFeedback && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className={`p-4 rounded-lg mb-4 ${actionFeedback.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white flex items-center justify-center`}
                aria-live="polite"
              >
                {actionFeedback.message}
              </motion.div>
            )}
            {Object.keys(groupByMatrizador).length === 0 ? (
              <p className="text-center text-lg text-gray-400">
                {filterStatus === 'Todos'
                  ? 'No hay comprobantes para el día seleccionado.'
                  : `No hay comprobantes con estado "${filterStatus}" para el día seleccionado.`}
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupByMatrizador).map(([matrizador, data]) => (
                  <motion.div
                    key={matrizador}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-notaria-blue-1 bg-opacity-80 rounded-xl shadow-lg overflow-hidden"
                  >
                    <button
                      className="flex justify-between items-center p-4 sm:p-6 w-full text-left cursor-pointer"
                      onClick={() => setExpandedMatrizador(expandedMatrizador === matrizador ? null : matrizador)}
                      aria-expanded={expandedMatrizador === matrizador}
                      aria-controls={`matrizador-${matrizador}`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className={`w-4 h-4 rounded-full ${data.statusColor}`} aria-hidden="true"></span>
                        <h3 className="text-lg sm:text-xl font-semibold">{matrizador}</h3>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={clsx(
                          'text-sm sm:text-base font-medium',
                          {
                            'text-green-300': data.statusColor.includes('green'),
                            'text-yellow-300': data.statusColor.includes('yellow'),
                            'text-red-300': data.statusColor.includes('red'),
                          }
                        )}>
                          {data.status}
                        </span>
                        <span className={clsx(
                          'text-sm sm:text-base font-medium px-2 py-1 rounded',
                          data.matrizadorStatusColor,
                          'text-white'
                        )}>
                          {data.matrizadorStatus}
                        </span>
                        {expandedMatrizador === matrizador ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-300" aria-hidden="true" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-300" aria-hidden="true" />
                        )}
                      </div>
                    </button>
                    <AnimatePresence>
                      {expandedMatrizador === matrizador && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="p-4 sm:p-6 bg-notaria-blue-2 bg-opacity-50"
                          id={`matrizador-${matrizador}`}
                        >
                          <div className="mb-6">
                            <h4 className="text-base sm:text-lg font-semibold mb-3 text-gray-200">Resumen</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm sm:text-base">
                              <p><span className="font-medium text-gray-300">Total Ajustado:</span> ${data.totalAjustado.toFixed(2)}</p>
                              <p><span className="font-medium text-gray-300">Total Comprobantes:</span> ${data.totalComprobantes.toFixed(2)}</p>
                              <p><span className="font-medium text-gray-300">Trámites:</span> {data.tramites.length}</p>
                              <p><span className="font-medium text-gray-300">Comprobantes:</span> {data.comprobantes.length}</p>
                              <p><span className="font-medium text-gray-300">Total Comisión Auto:</span> ${data.totalComisionAuto.toFixed(2)}</p>
                              <p><span className="font-medium text-gray-300">Total Reconocimientos:</span> {data.totalReconocimientos}</p>
                            </div>
                          </div>

                          <div className="mb-6">
                            <h4 className="text-base sm:text-lg font-semibold mb-3 text-gray-200">Comprobantes</h4>
                            {data.comprobantes.length === 0 ? (
                              <p className="text-sm sm:text-base text-gray-400">No hay comprobantes asociados.</p>
                            ) : (
                              <div className="space-y-3">
                                {data.comprobantes.map(comprobante => (
                                  <div key={comprobante.id} className="bg-notaria-blue-1 bg-opacity-70 p-4 rounded-lg">
                                    <div className="flex justify-between items-center mb-2">
                                      <span className={clsx(
                                        'text-sm font-medium px-2 py-1 rounded',
                                        comprobante.statusColor,
                                        'text-white'
                                      )}>
                                        {comprobante.status}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm sm:text-base">
                                      <p><span className="font-medium text-gray-300">Comprobante #:</span> {comprobante.id}</p>
                                      <p><span className="font-medium text-gray-300">Tipo de Pago:</span> {comprobante.tipo_pago}</p>
                                      <p><span className="font-medium text-gray-300">Número de Transacción:</span> {comprobante.numero_transaccion}</p>
                                      <p><span className="font-medium text-gray-300">Fecha de Depósito:</span> {comprobante.fecha_deposito}</p>
                                      <p><span className="font-medium text-gray-300">Monto Total:</span> ${parseFloat(comprobante.monto_total || 0).toFixed(2)}</p>
                                      <p>
                                        <span className="font-medium text-gray-300">Archivo:</span>{' '}
                                        <button
                                          onClick={() => handleViewFile(comprobante.archivo, comprobante.archivo.toLowerCase().endsWith('.pdf'))}
                                          className="text-notaria-yellow hover:underline focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                          aria-label={`Ver comprobante ${comprobante.id}`}
                                        >
                                          {showFileViewer && selectedFile === comprobante.archivo && !comprobante.archivo.toLowerCase().endsWith('.pdf') ? 'Ocultar Comprobante' : 'Ver Comprobante'}
                                        </button>
                                      </p>
                                    </div>
                                    {showFileViewer && selectedFile === comprobante.archivo && !comprobante.archivo.toLowerCase().endsWith('.pdf') && (
                                      <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.3 }}
                                        className="mt-4 bg-white rounded-lg p-4"
                                      >
                                        <div className="flex justify-between items-center mb-2">
                                          <h4 className="text-base font-semibold text-black">Vista del Comprobante</h4>
                                          <div className="flex items-center space-x-2">
                                            <motion.button
                                              onClick={handleZoomOut}
                                              className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                              whileHover={{ scale: 1.1 }}
                                              whileTap={{ scale: 0.9 }}
                                              aria-label="Reducir zoom"
                                            >
                                              <MagnifyingGlassMinusIcon className="w-5 h-5" />
                                            </motion.button>
                                            <motion.button
                                              onClick={handleZoomIn}
                                              className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                              whileHover={{ scale: 1.1 }}
                                              whileTap={{ scale: 0.9 }}
                                              aria-label="Aumentar zoom"
                                            >
                                              <MagnifyingGlassPlusIcon className="w-5 h-5" />
                                            </motion.button>
                                            <a
                                              href={selectedFile}
                                              download
                                              className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                              aria-label="Descargar comprobante"
                                            >
                                              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                                <DocumentArrowDownIcon className="w-5 h-5" />
                                              </motion.div>
                                            </a>
                                            <motion.button
                                              onClick={() => setShowFileViewer(false)}
                                              className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                              whileHover={{ scale: 1.1 }}
                                              whileTap={{ scale: 0.9 }}
                                              aria-label="Cerrar visor de archivo"
                                            >
                                              <XMarkIcon className="w-5 h-5" />
                                            </motion.button>
                                          </div>
                                        </div>
                                        {fileError ? (
                                          <div className="text-center text-red-600">
                                            <p>{fileError}</p>
                                            <a
                                              href={selectedFile}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-notaria-yellow hover:underline mt-2 inline-block"
                                              aria-label="Abrir archivo en una nueva pestaña"
                                            >
                                              Abrir en una nueva pestaña
                                            </a>
                                          </div>
                                        ) : (
                                          <div className="flex justify-center">
                                            <img
                                              src={selectedFile}
                                              alt="Comprobante"
                                              className="max-w-full max-h-[50vh] object-contain"
                                              style={{ transform: `scale(${zoomLevel})`, transition: 'transform 0.3s ease' }}
                                              onError={onImageLoadError}
                                            />
                                          </div>
                                        )}
                                      </motion.div>
                                    )}
                                    {comprobante.status === 'Pendiente' && (
                                      <div className="mt-3 flex space-x-3">
                                        <motion.button
                                          onClick={() => handleApproveComprobante(comprobante.id, matrizador)}
                                          disabled={processingComprobante === comprobante.id || isLoadingApprove}
                                          className={clsx(
                                            'bg-blue-500 text-white py-1 px-3 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all flex items-center',
                                            processingComprobante === comprobante.id || isLoadingApprove ? 'opacity-50 cursor-not-allowed' : ''
                                          )}
                                          whileHover={{ scale: processingComprobante === comprobante.id || isLoadingApprove ? 1 : 1.05 }}
                                          whileTap={{ scale: processingComprobante === comprobante.id || isLoadingApprove ? 1 : 0.95 }}
                                          aria-label={`Aprobar comprobante ${comprobante.id}`}
                                          aria-busy={processingComprobante === comprobante.id}
                                        >
                                          {processingComprobante === comprobante.id ? (
                                            <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" aria-hidden="true" />
                                          ) : (
                                            <CheckCircleIcon className="w-5 h-5 inline mr-2" aria-hidden="true" />
                                          )}
                                          Aprobar
                                        </motion.button>
                                        <motion.button
                                          onClick={() => setConfirmAction({ type: 'reject', matrizador, comprobanteId: comprobante.id })}
                                          disabled={processingComprobante === comprobante.id || isLoadingApprove}
                                          className={clsx(
                                            'bg-notaria-red text-white py-1 px-3 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all flex items-center',
                                            processingComprobante === comprobante.id || isLoadingApprove ? 'opacity-50 cursor-not-allowed' : ''
                                          )}
                                          whileHover={{ scale: processingComprobante === comprobante.id || isLoadingApprove ? 1 : 1.05 }}
                                          whileTap={{ scale: processingComprobante === comprobante.id || isLoadingApprove ? 1 : 0.95 }}
                                          aria-label={`Rechazar comprobante ${comprobante.id}`}
                                          aria-busy={processingComprobante === comprobante.id}
                                        >
                                          {processingComprobante === comprobante.id ? (
                                            <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" aria-hidden="true" />
                                          ) : (
                                            <ExclamationCircleIcon className="w-5 h-5 inline mr-2" aria-hidden="true" />
                                          )}
                                          Rechazar
                                        </motion.button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div>
                            <h4 className="text-base sm:text-lg font-semibold mb-3 text-gray-200">Trámites Asociados</h4>
                            <div className="max-w-full overflow-x-auto">
                              <table role="grid" aria-describedby={`tramites-table-description-${matrizador}`} className="w-full border-collapse">
                                <caption id={`tramites-table-description-${matrizador}`} className="sr-only">
                                  Lista de trámites asociados al matrizador {matrizador} para el día {selectedDay}
                                </caption>
                                <thead>
                                  <tr className="bg-notaria-blue-1 text-white">
                                    <th className="p-2 text-left">No. Temporal</th>
                                    <th className="p-2 text-left">No. Comprobante</th>
                                    <th className="p-2 text-left">Fecha Documento</th>
                                    <th className="p-2 text-left">Objeto Escritura</th>
                                    <th className="p-2 text-left">Reconocimientos</th>
                                    <th className="p-2 text-left">Comisión Auto</th>
                                    <th className="p-2 text-left">Total Ajustado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {data.tramites.map(tramite => (
                                    <tr key={tramite.id} className="border-b border-notaria-blue-2">
                                      <td className="p-2">{tramite.no_temporal}</td>
                                      <td className="p-2">{tramite.no_comprobante || '-'}</td>
                                      <td className="p-2">{tramite.fecha_documento}</td>
                                      <td className="p-2">
                                        <div className="max-h-[80px] overflow-y-auto text-sm">
                                          {(tramite.items || []).map((item, index) => (
                                            <div key={index} className="py-0.5">{item.descripcion}</div>
                                          ))}
                                        </div>
                                      </td>
                                      <td className="p-2">{tramite.num_reconocimientos}</td>
                                      <td className="p-2">${tramite.comision_auto.toFixed(2)}</td>
                                      <td className="p-2">${tramite.total_ajustado.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {data.status === 'Cuadrado' && !data.allFinalizados && (
                            <div className="mt-6 flex space-x-4">
                              <motion.button
                                onClick={() => handleApproveMatrizador(matrizador)}
                                className={clsx(
                                  'w-full bg-blue-500 text-white font-bold py-2 rounded-lg',
                                  'hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all duration-300',
                                  'flex items-center justify-center',
                                  {
                                    'opacity-50 cursor-not-allowed': isLoadingApprove,
                                  }
                                )}
                                whileHover={{ scale: isLoadingApprove ? 1 : 1.02 }}
                                whileTap={{ scale: isLoadingApprove ? 1 : 0.98 }}
                                disabled={isLoadingApprove}
                                aria-label={`Aprobar todos los depósitos de ${matrizador}`}
                              >
                                <CheckCircleIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                                Aprobar Todos
                              </motion.button>
                              <motion.button
                                onClick={() => handleRejectMatrizador(matrizador)}
                                className={clsx(
                                  'w-full bg-notaria-red text-white font-bold py-2 rounded-lg',
                                  'hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all duration-300',
                                  'flex items-center justify-center',
                                  {
                                    'opacity-50 cursor-not-allowed': isLoadingApprove,
                                  }
                                )}
                                whileHover={{ scale: isLoadingApprove ? 1 : 1.02 }}
                                whileTap={{ scale: isLoadingApprove ? 1 : 0.98 }}
                                disabled={isLoadingApprove}
                                aria-label={`Rechazar todos los depósitos de ${matrizador}`}
                              >
                                <ExclamationCircleIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                                Rechazar Todos
                              </motion.button>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </main>

      <AnimatePresence>
        {confirmAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-notaria-blue-1 bg-opacity-95 p-6 rounded-2xl shadow-2xl w-full max-w-md"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">
                  {confirmAction.type === 'approve' ? 'Aprobar' : 'Rechazar'} {confirmAction.comprobanteId ? `Comprobante #${confirmAction.comprobanteId}` : `Todos los Comprobantes de ${confirmAction.matrizador}`}
                </h3>
                <motion.button
                  onClick={() => setConfirmAction(null)}
                  className="text-gray-300 hover:text-notaria-yellow transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Cerrar modal"
                >
                  <XMarkIcon className="w-6 h-6" />
                </motion.button>
              </div>
              {confirmAction.type === 'reject' && (
                <div className="mb-4">
                  <label htmlFor="rejectionNotes" className="block text-sm font-semibold mb-1">
                    Motivo del Rechazo (Opcional)
                  </label>
                  <textarea
                    id="rejectionNotes"
                    value={rejectionNotes}
                    onChange={(e) => setRejectionNotes(e.target.value)}
                    className="w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    rows="4"
                    placeholder="Escribe el motivo del rechazo aquí..."
                    aria-label="Motivo del rechazo"
                  />
                </div>
              )}
              <div className="flex justify-end space-x-4">
                <motion.button
                  onClick={() => setConfirmAction(null)}
                  className="bg-notaria-blue-2 text-white py-2 px-4 rounded-lg hover:bg-notaria-blue-3 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Cancelar"
                >
                  Cancelar
                </motion.button>
                <motion.button
                  onClick={handleConfirmAction}
                  disabled={isLoadingApprove || processingComprobante}
                  className={clsx(
                    confirmAction.type === 'approve' ? 'bg-blue-500' : 'bg-notaria-red',
                    'text-white py-2 px-4 rounded-lg hover:bg-notaria-yellow hover:text-notaria-blue-1 transition-all flex items-center',
                    isLoadingApprove || processingComprobante ? 'opacity-50 cursor-not-allowed' : ''
                  )}
                  whileHover={{ scale: isLoadingApprove || processingComprobante ? 1 : 1.05 }}
                  whileTap={{ scale: isLoadingApprove || processingComprobante ? 1 : 0.95 }}
                  aria-label={confirmAction.type === 'approve' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
                  aria-busy={isLoadingApprove || processingComprobante}
                >
                  {isLoadingApprove || processingComprobante ? (
                    <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" aria-hidden="true" />
                  ) : confirmAction.type === 'approve' ? (
                    <CheckCircleIcon className="w-5 h-5 inline mr-2" aria-hidden="true" />
                  ) : (
                    <ExclamationCircleIcon className="w-5 h-5 inline mr-2" aria-hidden="true" />
                  )}
                  {confirmAction.type === 'approve' ? 'Aprobar' : 'Rechazar'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default ApproveDeposits;